/** Message lists of a Nest validation error are joined with this into one line. */
const MESSAGE_SEPARATOR = '; ';
/** A body nested deeper than this is not read: no Nest shape goes past two levels. */
const MAX_DEPTH = 3;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function readMessage(value: unknown, depth: number): string | undefined {
    if (typeof value === 'string') return value.trim() || undefined;
    if (Array.isArray(value)) {
        // class-validator puts one string per failed rule; anything else is not text to show.
        const lines = value.flatMap((entry) =>
            typeof entry === 'string' && entry.trim() ? [entry.trim()] : []
        );
        return lines.length ? lines.join(MESSAGE_SEPARATOR) : undefined;
    }
    if (isRecord(value) && 'message' in value && depth < MAX_DEPTH) {
        return readMessage(value.message, depth + 1);
    }
    return undefined;
}

/**
 * The message of a parsed JSON error body from the API. Nest sends `message` as a string, or
 * as a list of strings when request validation fails; a list is joined into one line. An
 * object's own `message` is read in turn. Undefined when there is no text to show.
 */
export function errorBodyMessage(body: unknown): string | undefined {
    return isRecord(body) && 'message' in body ? readMessage(body.message, 1) : undefined;
}

/**
 * The message of a JSON error body from the API. The generated client puts the parsed body
 * on the error's own `message`, so the text sits at `error.message.message`. A plain string
 * `error.message` (a network error) is not a body and gives undefined. It reads any status:
 * text for the user goes through `ApiErrorMessage`, which keeps a 5xx body out of sight.
 */
export function nestedErrorMessage(error: unknown): string | undefined {
    if (!isRecord(error) || !('message' in error)) return undefined;
    return errorBodyMessage(error.message);
}
